<template>
  <MediumModal
    :is-open="open"
    @close="close"
  >
    <template #header>
      <span v-if="!confirmed"> Get Ready to Count! </span>
      <span v-else> Create Inventory Count </span>
    </template>

    <div>
      <div
        v-if="!confirmed"
        class="p-2"
      >
        <p class="mb-4 leading-tight">
          Salve is preparing a snapshot of your inventory. Any orders fulfilled during this count will result in
          incorrect inventory levels. Please ensure the following before starting:
        </p>
        <ul class="list-disc text-sm pl-6">
          <li>No orders are being bagged for delivery or pickup during the count.</li>
          <li>No new orders are being fulfilled from the point-of-sale.</li>
          <li>
            In retail, all delivery orders are bagged and ready for pickup before counting. Double-check before
            initiating the count to see no new delivery tickets have been printed.
          </li>
          <li>All the products are physically organized by Batch and Lot numbers.</li>
          <li>
            All transfers from today that have been physically received are marked as "Received" on the transfer record.
          </li>
        </ul>
        <p class="mt-4">When ready, click the "Get Started" button to begin counting.</p>
      </div>
      <div
        v-if="confirmed"
        class="grid grid-cols-2 gap-5"
      >
        <div class="input-group col-span-1 relative">
          <label for="count_date"> Count Date </label>
          <DatePicker
            color="gray"
            title-position="right"
            v-model="form.initiated_at"
          >
            <template v-slot="{ inputValue, inputEvents }">
              <div class="relative">
                <CalendarIcon
                  class="top-2.5 left-3 h-4 w-4 text-blue-600 absolute"
                  aria-hidden="true"
                />
                <input
                  id="count_date"
                  type="text"
                  class="pl-8"
                  :value="inputValue"
                  v-on="inputEvents"
                />
              </div>
            </template>
          </DatePicker>
          <p
            v-if="getErrors('date', form)"
            class="input-error"
          >
            {{ getErrors('date', form) }}
          </p>
        </div>
        <div class="input-group col-span-1 relative">
          <label for="count_time"> Count Time </label>
          <DatePicker
            color="gray"
            mode="time"
            v-model="form.initiated_at"
            :timezone="companyTimezone"
          >
            <template v-slot="{ inputValue, inputEvents }">
              <div class="relative">
                <ClockIcon
                  class="top-2.5 left-3 h-4 w-4 text-blue-600 absolute"
                  aria-hidden="true"
                />
                <input
                  id="count_time"
                  type="text"
                  class="pl-8"
                  :value="inputValue"
                  v-on="inputEvents"
                />
              </div>
            </template>
          </DatePicker>
          <p
            v-if="getErrors('time', form)"
            class="input-error"
          >
            {{ getErrors('time', form) }}
          </p>
        </div>

        <div class="input-group">
          <label for="locations"> Location </label>
          <div class="input-control">
            <select v-model="form.location_id">
              <option hidden>Select Location</option>
              <option
                v-for="location in locations"
                :value="location.id"
              >
                {{ location.name }}
              </option>
            </select>
          </div>
          <p
            v-if="getErrors('location_id', form)"
            class="input-error"
          >
            {{ getErrors('location_id', form) }}
          </p>
        </div>

        <div class="input-group">
          <MultiSelectInput
            v-model="form.spaces"
            :options="spaceOptions"
            placeholder="Select Spaces"
          >
            Spaces
          </MultiSelectInput>
        </div>

        <div class="input-group">
          <label for="locations"> Type </label>
          <div class="input-control">
            <select v-model="form.type">
              <option hidden>Count Type</option>
              <option
                v-for="(type, val) in types"
                :value="val"
              >
                {{ type }}
              </option>
            </select>
          </div>
          <p
            v-if="getErrors('type', form)"
            class="input-error"
          >
            {{ getErrors('type', form) }}
          </p>
        </div>

        <!--                <div class="input-group">-->
        <!--                    <label for="locations">-->
        <!--                        Method-->
        <!--                    </label>-->
        <!--                    <div class="input-control">-->
        <!--                        <select v-model="form.method">-->
        <!--                            <option hidden>Count Method</option>-->
        <!--                            <option v-for="(method, val) in methods" :value="val">-->
        <!--                                {{ method }}-->
        <!--                            </option>-->
        <!--                        </select>-->
        <!--                    </div>-->
        <!--                    <p v-if="getErrors('method', form)" class="input-error">-->
        <!--                        {{ getErrors('method', form) }}-->
        <!--                    </p>-->
        <!--                </div>-->

        <div class="input-group col-span-2 relative">
          <label for="count_name"> Inventory Count Name </label>
          <input
            v-model="form.name"
            type="text"
            id="count_name"
            :placeholder="placeholderName"
          />
          <p
            v-if="getErrors('name', form)"
            class="input-error"
          >
            {{ getErrors('name', form) }}
          </p>
        </div>

        <div class="input-group-checkbox col-span-2">
          <div class="input-checkbox-container">
            <input
              v-model="hasMaxQuantity"
              id="has_max_quantity"
              name="has_max_quantity"
              type="checkbox"
            />
          </div>
          <div class="input-label-container">
            <label for="has_max_quantity"> Count Low Inventory Stock Only. </label>
          </div>
        </div>

        <div
          v-if="hasMaxQuantity"
          class="input-group col-span-2"
        >
          <label for="count_name"> With quantity up to: </label>
          <input
            v-model="form.max_quantity"
            type="number"
            step="1"
            min="1"
            id="max_quantity"
            placeholder="50"
          />
          <p
            v-if="getErrors('max_quantity', form)"
            class="input-error"
          >
            {{ getErrors('max_quantity', form) }}
          </p>
        </div>

        <div class="input-group">
          <MultiSelectInput
            v-model="form.brands"
            :options="brandOptions"
            placeholder="Select Brands"
          >
            Brands
          </MultiSelectInput>
        </div>

        <div class="input-group">
          <MultiSelectInput
            v-model="form.categories"
            :options="categoryOptions"
            placeholder="Select Categories"
          >
            Categories
          </MultiSelectInput>
        </div>

        <div
          v-if="subCategoryOptions"
          class="input-group"
        >
          <MultiSelectInput
            v-model="form.sub_categories"
            :options="subCategoryOptions"
            placeholder="Select Sub-Categories"
          >
            Sub-Categories
          </MultiSelectInput>
        </div>

        <div class="input-group-checkbox col-span-2">
          <div class="input-checkbox-container">
            <input
              v-model="form.published_only"
              id="published_only"
              name="published_only"
              type="checkbox"
            />
          </div>
          <div class="input-label-container">
            <label for="published_only"> Published inventory only </label>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <button
        v-if="!confirmed"
        @click.prevent="confirm()"
        type="button"
        class="btn inline-flex items-center px-4 py-2 bg-red-600 min-w-[200px] text-gray-100"
      >
        Get Started
      </button>
      <button
        v-else
        @click.prevent="submit()"
        :disabled="creating"
        type="button"
        class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-gray-100 bg-blue-600 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600 min-w-[200px]"
      >
        {{ submitText }}
      </button>
      <button
        @click="close()"
        type="button"
        class="mt-3 inline-flex justify-center rounded-md border border-gray-400 px-4 py-2 bg-gray-100 font-medium text-gray-700 shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600 sm:mt-0 ml-3 w-auto text-sm"
      >
        Cancel
      </button>
    </template>
  </MediumModal>
</template>

<script>
import MediumModal from '@/Components/Modals/MediumModal.vue'
import {
  ExclamationCircleIcon,
  XCircleIcon,
  SearchIcon,
  ChevronDownIcon,
  CalendarIcon,
  ClockIcon
} from '@heroicons/vue/solid'
import { PlusCircleIcon, MinusCircleIcon } from '@heroicons/vue/outline'
import getsErrors from '@/Mixins/getsErrors'
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import interactsWithCurrentLocation from '@/Mixins/interactsWithCurrentLocation'
import accessesCompany from '@/Mixins/accessesCompany'
import { Calendar, DatePicker } from 'v-calendar'
import moment from 'moment'
import MultiSelectInput from '@/Components/Forms/MultiSelectInput'
import { getHeadersWithToken } from '@/utils/http'
import _ from 'lodash'

export default {
  mixins: [getsErrors, interactsWithCurrentLocation, accessesCompany],

  components: {
    MediumModal,
    ExclamationCircleIcon,
    XCircleIcon,
    SearchIcon,
    PlusCircleIcon,
    MinusCircleIcon,
    ChevronDownIcon,
    CalendarIcon,
    ClockIcon,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    Calendar,
    DatePicker,
    MultiSelectInput
  },

  props: {
    open: {
      type: Boolean,
      default: false
    },
    actionText: {
      type: String,
      default: 'Save'
    },
    types: {
      type: Object,
      default() {
        return null
      }
    },
    methods: {
      type: Object,
      default() {
        return null
      }
    },
    brands: {
      type: Array,
      default() {
        return []
      }
    },
    categories: {
      type: Array,
      default() {
        return []
      }
    },
    subCategories: {
      type: Array,
      default() {
        return []
      }
    }
  },

  emits: ['update:open', 'created'],

  data() {
    return {
      confirmed: false,
      creating: false,
      hasMaxQuantity: false,
      spaces: [],
      form: this.$inertia.form({
        location_id: null,
        spaces: [],
        initiated_at: new Date(),
        type: null,
        method: 'sub_lot',
        name: null,
        max_quantity: null,
        brands: [],
        categories: [],
        sub_categories: [],
        published_only: false
      })
    }
  },

  watch: {
    hasMaxQuantity(newVal) {
      if (newVal) {
        this.form.max_quantity = 50
      } else {
        this.form.max_quantity = null
      }
    },
    'form.location_id'(newVal) {
      if (newVal) {
        this.form.spaces = []
        this.getSpacesForLocation(newVal)
      } else {
        this.spaces = []
        this.form.spaces = []
      }
    },
    'form.categories'(newVal) {
      this.form.sub_categories = []
    }
  },

  computed: {
    errors() {
      return this.$page.props.errors.createCount || {}
    },
    submitText() {
      if (this.creating) {
        return 'Saving...'
      }

      return this.actionText
    },
    selectedLocation() {
      if (!this.form.location_id) {
        return null
      }

      return this.locations.find((location) => location.id === this.form.location_id)
    },
    placeholderName() {
      let name = []

      if (this.selectedLocation) {
        name.push(this.selectedLocation.name)
      }

      if (this.form.type) {
        name.push(this.form.type)
      }

      name.push(moment(new Date(this.form.initiated_at)).format('YYYYMMDD'))

      return name.join('-')
    },
    brandOptions() {
      return _.map(this.brands, (brand) => {
        return {
          value: brand.id,
          label: brand.name
        }
      })
    },
    categoryOptions() {
      return _.map(this.categories, (category) => {
        return {
          value: category.id,
          label: category.name
        }
      })
    },
    subCategoryOptions() {
      return _.map(
        _.filter(this.subCategories, (category) => {
          return this.form.categories.includes(category.parent_id)
        }),
        (category) => {
          return {
            value: category.id,
            label: category.name
          }
        }
      )
    },
    spaceOptions() {
      return _.sortBy(
        _.map(this.spaces, (space) => {
          return {
            value: space.id,
            label: space.name
          }
        }),
        ['label']
      )
    }
  },

  methods: {
    confirm() {
      this.confirmed = true
    },
    back() {
      this.confirmed = false
    },
    close() {
      this.$emit('update:open', false)
      setTimeout(() => {
        this.back()
      }, 1000)
    },
    submit() {
      this.submitCreate()
    },
    submitCreate() {
      this.creating = true

      this.form
        .transform((data) => ({
          ...data,
          name: data.name || this.placeholderName
        }))
        .post(route('counts.store'), {
          errorBag: 'createCount',
          onSuccess: (response) => {
            this.form.reset()
            this.emitCreated()
            this.creating = false
          },
          onFinish: (response) => {
            this.creating = false
          }
        })
    },
    emitCreated() {
      this.$emit('created')
    },
    async getSpacesForLocation(location_id) {
      const response = await fetch(route('api.locations.spaces', location_id), {
        headers: getHeadersWithToken(),
        method: 'get'
      })
      this.spaces = await response.json()
    }
  }
}
</script>
